/** @jsxImportSource @emotion/react */
import { Global } from '@emotion/react'
import React, { useCallback, useEffect, useState } from 'react'
import { Spin, TreeSelect } from 'antd'
import { DownOutlined, LoadingOutlined } from '@ant-design/icons'
import { Team } from '@sportninja/common/types/TeamStanding'
import colors from '@sportninja/common/constants/appColors'
import {
  OrganizationTreeNode,
  TreeScheduleNode,
} from 'src/context/MyScheduleContext/types'
import {
  categoryTitleStyle,
  popupGlobalStyles,
  treeSelectStyles,
  treeNodeStyle,
  treeNodeImageStyle,
  treeNodeTextStyle,
  treeNodeNameStyle,
  treeNodeSubtitleStyle,
  POPUP_CLASS,
} from './styles'
import { getPlaceholder, formatDateRange } from './utils'

interface TargetAudienceSelectorProps {
  orgTree: OrganizationTreeNode[]
  teams: Team[]
  selected: string | undefined
  setSelected: (value: any) => void
  selectedType: string | null
  setSelectedType: (value: string | null) => void
  targetAudience: number | null
  setTargetAudience: (value: number | null) => void
  setParentId: (value: string) => void
  setParentEntityType: (
    value: 'team' | 'schedule' | 'organization' | undefined
  ) => void
  disabled: boolean
  isLoadingEntities?: boolean
  hasMoreTeams?: boolean
  isLoadingMoreTeams?: boolean
  onLoadMoreTeams?: () => void
  onTeamsSearch?: (query: string) => void
}

/**
 * Target Audience Selector using Ant Design TreeSelect
 * Displays org hierarchy with nested schedules, plus flat teams.
 */
const TargetAudienceSelector: React.FC<TargetAudienceSelectorProps> = ({
  orgTree,
  teams,
  selected,
  setSelected,
  selectedType,
  setSelectedType,
  setTargetAudience,
  setParentId,
  setParentEntityType,
  disabled,
  isLoadingEntities,
  hasMoreTeams,
  isLoadingMoreTeams,
  onLoadMoreTeams,
  onTeamsSearch,
}) => {
  'use memo'

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [searchText, setSearchText] = useState('')

  useEffect(() => {
    if (!selected || !selectedType) {
      setTargetAudience(null)
    }
  }, [selected, selectedType, setTargetAudience])

  // Build a map from entity id to its type for quick lookup on change
  const entityTypeMap = (() => {
    const map: Record<string, string> = {}

    const walkTree = (nodes: OrganizationTreeNode[]) => {
      for (const node of nodes) {
        map[node.id] = node.schedule_type_id != null ? 'sched' : 'org'
        if (node.schedules) {
          for (const s of node.schedules) {
            map[s.id] = 'sched'
          }
        }
        if (node.children) {
          walkTree(node.children)
        }
      }
    }
    walkTree(orgTree)

    teams.forEach((t) => (map[t.id] = 'team'))
    return map
  })()

  // Build a name map so we can resolve label from id
  const entityNameMap = (() => {
    const map: Record<string, string> = {}

    const walkTree = (nodes: OrganizationTreeNode[]) => {
      for (const node of nodes) {
        map[node.id] = node.name_full || node.name
        if (node.schedules) {
          for (const s of node.schedules) {
            map[s.id] = s.name_full || s.name
          }
        }
        if (node.children) {
          walkTree(node.children)
        }
      }
    }
    walkTree(orgTree)

    teams.forEach((t) => (map[t.id] = t.name_full || t.name))
    return map
  })()

  const handleChange = (valueObj: { value: string; label: string } | null) => {
    if (!valueObj) {
      setSelected(undefined)
      setSelectedType(null)
      // Reset search when clearing selection via X button
      setSearchText('')
      if (onTeamsSearch) {
        onTeamsSearch('')
      }
      return
    }
    const value = valueObj.value
    const type = entityTypeMap[value]
    setSelected(value)
    setSelectedType(type)
    setParentId(value)
    setParentEntityType(
      type === 'org' ? 'organization' : type === 'sched' ? 'schedule' : 'team'
    )
  }

  // Helper: subtitle for schedule
  const getSchedSubtitle = (sched: TreeScheduleNode) => {
    const parts: string[] = []
    if (sched.starts_at && sched.ends_at) {
      parts.push(formatDateRange(sched.starts_at, sched.ends_at))
    }
    if (sched.membership?.role_name) {
      parts.push(sched.membership.role_name)
    }
    return parts.join(' · ')
  }

  // Helper: subtitle for team
  const getTeamSubtitle = (team: Team) => {
    const parts: string[] = []
    if ((team as any).organization_name) {
      parts.push((team as any).organization_name)
    }
    if ((team as any).sport_name) {
      parts.push((team as any).sport_name)
    }
    return parts.join(' · ')
  }

  /**
   * Recursively builds tree nodes for an org, its children, and its schedules
   */
  const buildOrgTreeNode = (org: OrganizationTreeNode): any => {
    const name = org.name_full || org.name
    const thumb = org.image_url || getPlaceholder('org')

    const children: any[] = []

    // Sub-orgs first
    if (org.children?.length) {
      const sorted = [...org.children].sort((a, b) =>
        (a.name_full || a.name || '').localeCompare(b.name_full || b.name || '')
      )
      for (const child of sorted) {
        children.push(buildOrgTreeNode(child))
      }
    }

    // Then schedules
    if (org.schedules?.length) {
      const sorted = [...org.schedules].sort((a, b) => {
        const da = new Date(a.starts_at || 0).getTime()
        const db = new Date(b.starts_at || 0).getTime()
        return db - da
      })
      for (const sched of sorted) {
        const sName = sched.name_full || sched.name
        const sThumb = sched.image_url || getPlaceholder('sched')
        const sSubtitle = getSchedSubtitle(sched)

        children.push({
          value: sched.id,
          title: sName,
          label: sName,
          data: {
            image_url: sThumb,
            name: sName,
            subtitle: sSubtitle,
            fallbackImage: getPlaceholder('sched'),
          },
          selectable: true,
        })
      }
    }

    return {
      value: org.id,
      title: name,
      label: name,
      data: {
        image_url: thumb,
        name,
        subtitle: org.membership?.role_name || '',
        fallbackImage: getPlaceholder('org'),
      },
      selectable: true,
      children: children.length > 0 ? children : undefined,
    }
  }

  /**
   * Recursively filter org tree nodes by search query.
   * Returns the node (with filtered children/schedules) if it or any descendant matches.
   */
  const filterOrgNode = (node: any, query: string): any | null => {
    const titleMatch = (node.title as string)?.toLowerCase().includes(query)

    // Recursively filter children
    const filteredChildren = (node.children || [])
      .map((child: any) => filterOrgNode(child, query))
      .filter(Boolean)

    // Keep node if it matches or has matching descendants
    if (titleMatch || filteredChildren.length > 0) {
      return {
        ...node,
        children:
          filteredChildren.length > 0 ? filteredChildren : node.children,
      }
    }

    return null
  }

  const treeData = (() => {
    const nodes: any[] = []
    const isSearching = searchText.trim().length > 0
    const query = searchText.toLowerCase()

    const orgCategoryLabel = 'Hierarchy'

    if (orgTree.length > 0) {
      const sorted = [...orgTree].sort((a, b) =>
        (a.name_full || a.name || '').localeCompare(b.name_full || b.name || '')
      )
      const builtOrgNodes = sorted.map((org) => buildOrgTreeNode(org))

      // When searching, filter org nodes locally
      const orgChildren = isSearching
        ? builtOrgNodes
            .map((node) => filterOrgNode(node, query))
            .filter(Boolean)
        : builtOrgNodes

      if (orgChildren.length > 0) {
        nodes.push({
          value: '__cat_orgs',
          title: orgCategoryLabel,
          label: orgCategoryLabel,
          selectable: false,
          children: orgChildren,
        })
      }
    }

    // Teams: always show all (they come from API search when searching)
    if (teams.length > 0) {
      const teamChildren = [...teams]
        .sort((a, b) =>
          (a.name_full || a.name || '').localeCompare(
            b.name_full || b.name || ''
          )
        )
        .map((t) => {
          const name = t.name_full || t.name
          const thumb = (t as any).image?.url || getPlaceholder('team')
          const subtitle = getTeamSubtitle(t)
          return {
            value: t.id,
            title: name,
            label: name,
            data: {
              image_url: thumb,
              name,
              subtitle,
              fallbackImage: getPlaceholder('team'),
            },
            selectable: true,
          }
        })

      // Add loading spinner node when loading more teams
      if (isLoadingMoreTeams) {
        teamChildren.push({
          value: '__teams_loading',
          title: 'Loading more teams...',
          label: 'Loading more teams...',
          data: {
            image_url: '',
            name: 'Loading more teams...',
            subtitle: '',
            fallbackImage: '',
            isLoadingNode: true,
          },
          selectable: false,
        } as any)
      }

      nodes.push({
        value: '__cat_teams',
        title: 'Teams',
        label: 'Teams',
        selectable: false,
        children: teamChildren,
      })
    }

    return nodes
  })()

  // Wrap dropdown to detect scroll near bottom for infinite loading.
  // TreeSelect doesn't support onPopupScroll, so we use dropdownRender
  // with onScrollCapture (capture phase catches non-bubbling scroll events
  // from the internal virtual list).
  const handleDropdownRender = useCallback(
    (menu: React.ReactElement) => {
      return (
        <div
          onScrollCapture={(e) => {
            const target = e.target as HTMLElement
            const distFromBottom =
              target.scrollHeight - target.scrollTop - target.clientHeight
            if (
              distFromBottom < 50 &&
              hasMoreTeams &&
              !isLoadingMoreTeams &&
              onLoadMoreTeams
            ) {
              onLoadMoreTeams()
            }
          }}
        >
          {menu}
        </div>
      )
    },
    [hasMoreTeams, isLoadingMoreTeams, onLoadMoreTeams]
  )

  // Custom tree node renderer (matches FilterBar pattern)
  const renderTreeNode = useCallback((nodeData: any) => {
    // Category headers have no data
    if (!nodeData.data) {
      return <span css={categoryTitleStyle}>{nodeData.title}</span>
    }

    // Loading indicator node
    if (nodeData.data.isLoadingNode) {
      return (
        <div css={treeNodeStyle}>
          <LoadingOutlined style={{ fontSize: 16, color: colors.NEUTRAL_50 }} />
          <span css={treeNodeNameStyle} style={{ marginLeft: 8 }}>
            Loading more teams...
          </span>
        </div>
      )
    }

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
      e.currentTarget.onerror = null
      e.currentTarget.src =
        nodeData.data.fallbackImage || nodeData.data.image_url
    }

    return (
      <div css={treeNodeStyle}>
        <img
          src={nodeData.data.image_url}
          alt={nodeData.data.name}
          css={treeNodeImageStyle}
          onError={handleImageError}
          loading='lazy'
        />
        <div css={treeNodeTextStyle}>
          <span css={treeNodeNameStyle}>{nodeData.title}</span>
          {nodeData.data.subtitle && (
            <span css={treeNodeSubtitleStyle}>{nodeData.data.subtitle}</span>
          )}
        </div>
      </div>
    )
  }, [])

  // Auto-expand __cat_orgs and first-level org IDs on mount
  useEffect(() => {
    if (orgTree.length > 0 && expandedKeys.length === 0) {
      const keys: React.Key[] = ['__cat_orgs']
      for (const org of orgTree) {
        keys.push(org.id)
      }
      setExpandedKeys(keys)
    }
  }, [orgTree]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = useCallback(
    (value: string) => {
      setSearchText(value)

      // Auto-expand teams category when searching so team results are visible
      if (value.trim().length > 0) {
        setExpandedKeys((prev) => {
          if (prev.includes('__cat_teams')) {
            return prev
          }
          return [...prev, '__cat_teams']
        })
      }

      if (onTeamsSearch) {
        onTeamsSearch(value)
      }
    },
    [onTeamsSearch]
  )

  return (
    <>
      <Global styles={popupGlobalStyles} />
      <TreeSelect
        labelInValue
        value={
          selected
            ? {
                value: selected,
                label: entityNameMap[selected] || selected,
              }
            : undefined
        }
        onChange={handleChange}
        treeData={treeData}
        placeholder='Choose Organization, Competition, or Team'
        showSearch
        onSearch={handleSearch}
        filterTreeNode={false}
        treeExpandedKeys={expandedKeys}
        onTreeExpand={(keys) => setExpandedKeys(keys)}
        treeTitleRender={renderTreeNode}
        popupClassName={POPUP_CLASS}
        disabled={disabled}
        allowClear
        treeExpandAction='click'
        dropdownStyle={{ maxHeight: 500, overflow: 'auto', minWidth: 500 }}
        popupMatchSelectWidth={false}
        open={dropdownOpen}
        onDropdownVisibleChange={setDropdownOpen}
        dropdownRender={handleDropdownRender}
        suffixIcon={
          isLoadingEntities ? (
            <Spin size='small' />
          ) : (
            <DownOutlined
              style={{ color: colors.NEUTRAL_50, cursor: 'pointer' }}
              onClick={() => setDropdownOpen((prev) => !prev)}
            />
          )
        }
        css={treeSelectStyles}
      />
    </>
  )
}

export default TargetAudienceSelector
